// ── Clear Series ("/cohorts/:cohort_id/sessions/clear") — FR-SESS-002 (LIVE) ──
// The backend POST /cohorts/:cohort_id/sessions/clear is NOW LIVE with the
// operator-decided "KEEP + DETACH" semantics: it CLEARS the future sessions and
// KEEPS-AND-DETACHES the invites/recordings/reports (they still exist, just
// detached — never deleted). coaching.sessions.clear-series.org is granted to
// super_admin + coach only (migration 0227); a SA clears any cohort's series, a
// coach only a cohort assigned to them (the backend enforces in-scope — a foreign
// cohort 404s-via-400 and is rendered as an honest not-found, never another
// cohort's data).
//
// This screen wires the confirm action through the readiness service +
// useClearSeriesMutation. Nothing clears without an EXPLICIT destructive confirm
// (a typed "CLEAR" acknowledgement gate), and every submission carries a fresh
// UUID Idempotency-Key (idempotent on replay). The honest result (how many future
// sessions cleared, the kept-and-detached note) and the honest error / denied /
// gated postures all render through <HonestState> / <Alert> — a denial or a gate
// NEVER renders blank.

import { Alert, Button, Input, Module, ModuleBody, ModuleHead, PageFrame, useToast } from "@vjal/ui";
import { useState } from "react";
import { Link } from "react-router-dom";

import { HonestState } from "../../components/honest-state";
import { destructiveButtonClass } from "../../components/ui-classes";
import { evaluateActionCapability } from "../../domain/capabilities";
import { useClearSeriesMutation } from "../../hooks/useClearSeriesMutation";
import { describeReportWriteError } from "../reports/reports-write";
import { liveRecordIdFromPath } from "../product-live-rows";
import type { ProductScreenContext } from "../product-route-contracts";
import { canAccessSessionsModule, isValidCohortId } from "./sessions-helpers";
import { ClearSeriesResult } from "./clear-series-result";

const CONFIRM_WORD = "CLEAR";

export function ClearSeriesPage({ role, routePath }: ProductScreenContext) {
  const moduleDenied = !canAccessSessionsModule(role);
  // sessions:clear-series is enabled for super_admin + coach (migration 0227).
  const capability = evaluateActionCapability(role, "sessions:clear-series");
  const cohortId = liveRecordIdFromPath(routePath, "cohorts");
  const validId = isValidCohortId(cohortId);

  const toast = useToast();
  const clear = useClearSeriesMutation(cohortId);

  // Explicit destructive acknowledgement: the operator must type CLEAR. An
  // optional reason is recorded in the audit metadata.
  const [acknowledgement, setAcknowledgement] = useState("");
  const [reason, setReason] = useState("");

  if (moduleDenied) {
    return (
      <ClearSeriesShell>
        <HonestState
          posture="permission-denied"
          title="You don't have access to clear sessions"
          message="Clearing a session series is restricted to super admins and the cohort's coach."
        />
      </ClearSeriesShell>
    );
  }

  // Not enabled for this role (admin / company_admin / cohort_lead / participant)
  // -> denied honestly; the destructive action never renders for them.
  if (capability.presentation !== "enabled") {
    return (
      <ClearSeriesShell>
        <HonestState
          posture="permission-denied"
          title="Clear Series is restricted"
          message="Only a super admin or the cohort's assigned coach can clear a session series."
        />
      </ClearSeriesShell>
    );
  }

  if (!validId) {
    return (
      <ClearSeriesShell>
        <HonestState posture="empty" title="No cohort selected" message="Open Clear Series from a cohort." />
      </ClearSeriesShell>
    );
  }

  const confirmed = acknowledgement.trim().toUpperCase() === CONFIRM_WORD;
  const writeError = describeReportWriteError(clear.error);
  const result = clear.data ?? null;

  function run() {
    if (!confirmed || !validId) return;
    clear.reset();
    clear.mutate(
      { confirm: true, reason: reason.trim() ? reason.trim() : undefined },
      { onSuccess: () => toast.success("Session series cleared") }
    );
  }

  return (
    <ClearSeriesShell>
      <Module span={12}>
        <ModuleHead
          title="Clear session series"
          subtitle="Clears the cohort's FUTURE sessions. Past sessions, invites, recordings, and reports are kept."
        />
        <ModuleBody className="space-y-4" data-testid="clear-series-form">
          <Alert variant="warning">
            This clears the cohort's <span className="font-semibold">not-yet-occurred future sessions</span>. Past
            sessions are left untouched, and the already-sent invites, recordings, and generated reports are{" "}
            <span className="font-semibold">kept and detached</span> as historical records — they are never deleted.
            This action requires an explicit confirmation.
          </Alert>

          {/* Honest result of a completed clear (counts + kept-and-detached note). */}
          {result ? <ClearSeriesResult result={result} /> : null}

          {/* Honest write error (denied / gate-closed / unavailable / validation). */}
          {writeError ? <Alert variant={writeError.tone}>{writeError.message}</Alert> : null}

          <div className="space-y-3">
            <Input
              label={`Type ${CONFIRM_WORD} to confirm`}
              aria-label="Clear series confirmation"
              value={acknowledgement}
              onChange={(event) => setAcknowledgement(event.target.value)}
              placeholder={CONFIRM_WORD}
            />
            <Input
              label="Reason (optional)"
              aria-label="Clear series reason"
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              placeholder="Why is this series being cleared?"
            />
            <div className="flex items-center gap-3">
              <Button
                type="button"
                variant="danger"
                className={destructiveButtonClass}
                disabled={!confirmed}
                loading={clear.isPending}
                onClick={run}
                aria-label="Clear series"
              >
                Clear future sessions
              </Button>
              {!confirmed ? (
                <span className="text-sm text-ink-muted">Type {CONFIRM_WORD} above to enable this action.</span>
              ) : null}
            </div>
          </div>
        </ModuleBody>
      </Module>
    </ClearSeriesShell>
  );
}

function ClearSeriesShell({ children }: { children: React.ReactNode }) {
  return (
    <PageFrame title="Clear session series" subtitle="KEEP + DETACH future sessions for this cohort. Explicit typed confirmation required." className="col-span-12">
      <section aria-label="Clear session series" className="flex flex-col gap-4" data-testid="clear-series-page">
        <Link to="/sessions" className="text-link no-underline hover:underline">
          ← Back to sessions
        </Link>
        <div className="rounded-xl border border-line bg-white p-0">{children}</div>
      </section>
    </PageFrame>
  );
}
